import type { ReactNode } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type ToastVariant = 'success' | 'error';
export type ToastLayout = 'default' | 'wide';

export interface ToastItem {
  id: string;
  /** Optional headline shown above the message (Figma: 18px SemiBold). */
  title?: string;
  message: string;
  /** When set, rendered instead of `message` (e.g. learners-added toast with bold names). */
  messageNode?: ReactNode;
  variant: ToastVariant;
  layout?: ToastLayout;
}

export interface ShowToastOptions {
  messageNode?: ReactNode;
  layout?: ToastLayout;
}

const TOAST_MS = 5200;

interface ToastState {
  toasts: ToastItem[];
  /**
   * Push a toast. Signature stays string-first for backward compatibility;
   * pass a `title` to render the Figma two-line variant, otherwise a generic
   * fallback title is used by the renderer.
   */
  show: (message: string, variant: ToastVariant, title?: string, options?: ShowToastOptions) => void;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>()(
  devtools(
    (set, get) => ({
      toasts: [],
      show: (message, variant, title, options) => {
        const id = crypto.randomUUID();
        const item: ToastItem = {
          id,
          message,
          variant,
          ...(title ? { title } : {}),
          ...(options?.messageNode ? { messageNode: options.messageNode } : {}),
          ...(options?.layout ? { layout: options.layout } : {}),
        };
        set((s) => ({ toasts: [...s.toasts, item] }));
        window.setTimeout(() => {
          get().dismiss(id);
        }, TOAST_MS);
      },
      dismiss: (id) => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      },
    }),
    { name: 'toast-store' },
  ),
);
